import React, { useState } from 'react';
import { FiUpload, FiAlertCircle, FiCheck, FiX, FiCalendar, FiInfo } from 'react-icons/fi';
import axios from 'axios';
import { convertExcelToCSV, validateInsuranceFile } from '../../../utils/excelConverter';
import { toast } from 'react-toastify';

const InsuranceImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileValid, setFileValid] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [endDateOffset, setEndDateOffset] = useState(365);
  const [results, setResults] = useState(null);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setFileName(selectedFile.name);
    setLoading(true);
    
    try {
      const validation = await validateInsuranceFile(selectedFile);
      setFileValid(validation.isValid);
      setValidationMessage(validation.message);
    } catch (error) {
      setFileValid(false);
      setValidationMessage(`Error validating file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !fileValid) return;
    
    setLoading(true);
    try {
      // Convert Excel to CSV if needed
      let fileToUpload = file;
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        const csvBlob = await convertExcelToCSV(file);
        fileToUpload = new File([csvBlob], `${file.name.split('.')[0]}.csv`, { type: 'text/csv' });
      }

      // Create form data
      const formData = new FormData();
      formData.append('insuranceFile', fileToUpload);
      formData.append('endDateOffset', endDateOffset);

      // Send to server
      const response = await axios.post('https://rent-server.create-site.gr/api/insurance/import', formData, {
        headers: {
            // authentication bearer
            Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setResults(response.data.results);
        toast.success(`Successfully updated insurance for ${response.data.results.success} vehicles`);
        if (response.data.results.failed > 0) {
          toast.warning(`Failed to update ${response.data.results.failed} vehicles. Check the results for details.`);
        }
        if (typeof onSuccess === 'function') {
          onSuccess(response.data.results);
        }
      } else {
        toast.error('Failed to import insurance data');
      }
    } catch (error) {
      console.error('Error importing insurance data:', error);
      toast.error(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-800">Import Insurance Data</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FiX size={24} />
            </button>
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-700 mb-2">Upload Insurance File</h3>
              <p className="text-gray-600 mb-4">
                Upload an Excel (.xlsx) or CSV file containing insurance data. 
                The file should have columns for license plate, insurance company, policy number, and start date.
              </p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center">
                <input
                  type="file"
                  id="insuranceFile"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label 
                  htmlFor="insuranceFile"
                  className="flex flex-col items-center justify-center cursor-pointer w-full"
                >
                  <FiUpload size={40} className="text-blue-500 mb-2" />
                  <span className="text-gray-700 font-medium">Click to select file</span>
                  <span className="text-gray-500 text-sm mt-1">Excel or CSV format</span>
                </label>
              </div>
              
              {fileName && (
                <div className={`mt-4 p-3 rounded-md flex items-center ${fileValid ? 'bg-green-50' : 'bg-red-50'}`}>
                  {fileValid ? (
                    <FiCheck className="text-green-500 mr-2 flex-shrink-0" size={20} />
                  ) : (
                    <FiAlertCircle className="text-red-500 mr-2 flex-shrink-0" size={20} />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${fileValid ? 'text-green-700' : 'text-red-700'}`}>{fileName}</p>
                    <p className={`text-sm ${fileValid ? 'text-green-600' : 'text-red-600'}`}>{validationMessage}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6">
              <div className="flex items-center mb-2">
                <FiCalendar className="mr-2 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-700">Insurance Duration</h3>
              </div>
              <p className="text-gray-600 mb-3">
                Set the default duration for insurance policies in days from the start date.
              </p>
              
              <div className="flex items-center">
                <input
                  type="number"
                  value={endDateOffset}
                  onChange={(e) => setEndDateOffset(Number(e.target.value))}
                  min="1"
                  max="1825" // 5 years
                  className="border border-gray-300 rounded-md px-3 py-2 w-24 text-right"
                />
                <span className="ml-2 text-gray-600">days</span>
                
                <div className="ml-4 text-sm text-gray-500 flex items-start">
                  <FiInfo className="mr-1 mt-0.5 text-gray-400 flex-shrink-0" />
                  <span>Default is 365 days (1 year)</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button 
                type="button" 
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md mr-2 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading || !fileValid}
                className={`px-4 py-2 text-white rounded-md ${loading || !fileValid 
                  ? 'bg-blue-300 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {loading ? 'Processing...' : 'Import Insurance Data'}
              </button>
            </div>
          </form>

          {results && (
            <div className="mt-6 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-700 mb-3">Import Results</h3>
              
              <div className="grid grid-cols-5 gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-700">Total Records</p>
                  <p className="text-2xl font-bold text-blue-800">{results.total}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-md">
                  <p className="text-sm text-green-700">Successfully Updated</p>
                  <p className="text-2xl font-bold text-green-800">{results.success}</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded-md">
                  <p className="text-sm text-yellow-700">Skipped</p>
                  <p className="text-2xl font-bold text-yellow-800">{results.skipped || 0}</p>
                </div>
                <div className="bg-red-50 p-3 rounded-md">
                  <p className="text-sm text-red-700">Failed</p>
                  <p className="text-2xl font-bold text-red-800">{results.failed}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-700">Processed</p>
                  <p className="text-2xl font-bold text-gray-800">{results.processed}</p>
                </div>
              </div>

              {results.errors && results.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-700 mb-2">Errors</h4>
                  <div className="max-h-40 overflow-y-auto border border-red-200 rounded-md">
                    <table className="min-w-full divide-y divide-red-200">
                      <thead className="bg-red-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase tracking-wider">License Plate</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700 uppercase tracking-wider">Error</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-red-200">
                        {results.errors.map((error, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-800">{error.record.licensePlate || 'N/A'}</td>
                            <td className="px-4 py-2 text-sm text-red-600">{error.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {results.updatedVehicles && results.updatedVehicles.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-700 mb-2">Successfully Updated Vehicles</h4>
                  <div className="max-h-60 overflow-y-auto border border-green-200 rounded-md">
                    <table className="min-w-full divide-y divide-green-200">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider">License Plate</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider">Original Plate</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider">Insurance Provider</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider">Policy Number</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider">Start Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider">End Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700 uppercase tracking-wider">Changes</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-green-200">
                        {results.updatedVehicles.map((vehicle, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-800">{vehicle.licensePlate}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{vehicle.originalPlate !== vehicle.licensePlate ? vehicle.originalPlate : '-'}</td>
                            <td className="px-4 py-2 text-sm text-gray-800">{vehicle.provider}</td>
                            <td className="px-4 py-2 text-sm text-gray-800">{vehicle.policyNumber || 'N/A'}</td>
                            <td className="px-4 py-2 text-sm text-gray-800">{vehicle.startDate}</td>
                            <td className="px-4 py-2 text-sm text-gray-800">{vehicle.endDate}</td>
                            <td className="px-4 py-2 text-sm text-green-600">
                              {vehicle.changes && vehicle.changes.length > 0 ? (
                                <div className="text-xs text-gray-600">{vehicle.changes.join(', ')}</div>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {results.skippedVehicles && results.skippedVehicles.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-700 mb-2">Skipped Vehicles</h4>
                  <div className="max-h-40 overflow-y-auto border border-yellow-200 rounded-md">
                    <table className="min-w-full divide-y divide-yellow-200">
                      <thead className="bg-yellow-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-yellow-700 uppercase tracking-wider">License Plate</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-yellow-700 uppercase tracking-wider">Provider</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-yellow-700 uppercase tracking-wider">Policy Number</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-yellow-700 uppercase tracking-wider">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-yellow-200">
                        {results.skippedVehicles.map((vehicle, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-800">{vehicle.licensePlate}</td>
                            <td className="px-4 py-2 text-sm text-gray-800">{vehicle.provider}</td>
                            <td className="px-4 py-2 text-sm text-gray-800">{vehicle.policyNumber || 'N/A'}</td>
                            <td className="px-4 py-2 text-sm text-yellow-600">{vehicle.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {results.normalizedPlates && results.normalizedPlates.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-700 mb-2">Normalized License Plates</h4>
                  <div className="max-h-40 overflow-y-auto border border-blue-200 rounded-md">
                    <table className="min-w-full divide-y divide-blue-200">
                      <thead className="bg-blue-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Original</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">Normalized</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-blue-200">
                        {results.normalizedPlates.map((plate, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-800">{plate.original}</td>
                            <td className="px-4 py-2 text-sm text-blue-600">{plate.normalized}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {results.logEntries && results.logEntries.length > 0 && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-700">Detailed Processing Logs</h4>
                    <button 
                      onClick={() => {
                        const logText = results.logEntries
                          .map(log => `[${log.timestamp}] [${log.action}] ${log.licensePlate}: ${log.message}`)
                          .join('\n');
                        
                        const blob = new Blob([logText], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'insurance-import-log.txt';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                      className="text-sm px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      Download Logs
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md bg-gray-50 p-3">
                    <div className="font-mono text-xs text-gray-700 whitespace-pre-wrap">
                      {results.logEntries.map((log, index) => {
                        let colorClass = 'text-gray-700';
                        if (log.action === 'ERROR') colorClass = 'text-red-600';
                        if (log.action === 'SUCCESS') colorClass = 'text-green-600';
                        if (log.action === 'SKIP') colorClass = 'text-yellow-600';
                        if (log.action === 'UPDATE') colorClass = 'text-blue-600';
                        
                        return (
                          <div key={index} className={`${colorClass} mb-1`}>
                            <span className="text-gray-500">[{new Date(log.timestamp).toLocaleTimeString()}]</span>{' '}
                            <span className="font-semibold">[{log.action}]</span>{' '}
                            <span className="text-gray-800">{log.licensePlate}:</span>{' '}
                            {log.message}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsuranceImportModal;
